import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    type Control,
    type UseFormReturn,
    type UseFormWatch,
} from 'react-hook-form';

import { DemoCallButton } from '@apps/web/components/demo_call_button';
import { resolveCarePlanSummary } from '@apps/web/components/outreaches/care_plan_summary_preview';
import { OutreachStepDelayInput } from '@apps/web/components/outreaches/outreach_step_delay_input';
import {
    getTemplateVariablePreviewValues,
    TEMPLATE_VARIABLE_ICONS,
} from '@apps/web/components/shared/messages/template_variable_metadata';
import {
    AvailableVariables,
    type TemplateVariable,
    VariableTextDisplay,
    VariableTextEditor,
} from '@apps/web/components/shared/messages/variable_text_editor';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useSpammyFollowUpConfirmation } from '@apps/web/hooks/use_spammy_follow_up_confirmation';
import {
    formatDate,
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import { sortShiftsByStartTime } from '@apps/web/lib/outreach_shift_ordering';
import {
    appendOutreachStep,
    getDefaultVoiceOpeningLine,
    getStepVoiceOpeningLineValue,
    getVisibleOutreachSteps,
    type OutreachType,
    removeOutreachStep,
} from '@apps/web/lib/outreach_step_editor_utils';
import {
    buildTemplateContext,
    buildVoiceTemplateContext,
    CONSOLIDATED_SHIFT_SMS_TEMPLATE,
    DEFAULT_OPEN_SHIFT_TEMPLATES,
    DEFAULT_RECURRING_TEMPLATES,
    generateGroupedMultiShiftSmsPreview,
    generateRecurrenceDescription,
    getCareCoordinatorNameFromShifts,
    MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE,
    type OpenShiftTemplates,
    type OutreachStep,
    RECURRING_CONSOLIDATED_SHIFT_SMS_TEMPLATE,
    RECURRING_SHIFT_PER_GROUP_SMS_TEMPLATE,
    resolveContiguousShiftPreview,
    resolveShiftGroupModesByKey,
    type ShiftGroupMode,
    shiftToPreviewDataShortDate,
    substituteTemplateVariables,
    substituteTemplateVariablesExceptZipcode,
    trimPatientNameAnnotations,
} from '@apps/web/lib/outreach_utils';
import {
    getOutreachTemplateVariableGroups,
    mergeTemplateVariables,
    organizationSupportsShiftRecurrence,
    withTemplateVariableAliases,
} from '@apps/web/lib/template_variables';
import {
    useOrganizationVoiceSettings,
    useTemplateVariableManifest,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ClientReferenceTerm,
    type Organization,
    type Shift,
} from '@libraries/typescript/api_client';

const OUTREACH_VARIABLE_GROUP_DEFINITIONS: ReadonlyArray<{
    readonly label: string;
    readonly tokens: ReadonlyArray<string>;
}> = [
    {
        label: 'Caregiver',
        tokens: ['caregiver_name', 'caregiver_first_name'],
    },
    {
        label: 'Shift',
        tokens: [
            'shift_date',
            'shift_day_of_week',
            'shift_duration',
            'shift_start_time',
            'shift_end_time',
            'shift_count',
            'shift_list',
            'shift_frequency',
        ],
    },
    {
        label: 'Client',
        tokens: [
            'client_reference_term',
            'client_name',
            'client_initials',
            'client_first_initial',
            'client_last_initial',
            'client_first_name',
            'client_last_name',
            'patient_name',
            'patient_first_name',
            'patient_last_name',
            'client_city',
            'patient_city',
            'client_address',
            'patient_address',
            'client_street_name',
            'patient_street_name',
            'client_zip_code',
            'patient_zip_code',
            'location_text',
        ],
    },
    {
        label: 'Care team',
        tokens: [
            'organization_name',
            'care_coordinator',
            'caregiver_distance',
            'previous_visits',
            'care_plan_summary',
            'phoebe_intro',
        ],
    },
    {
        label: 'Grouped shifts',
        tokens: [
            'client_groups',
            'shift_groups',
            'group_number',
            'group_acceptance_requirement',
            'schedule_summary',
            'start_date',
        ],
    },
    {
        label: 'Admin details',
        tokens: ['role_required', 'time_and_attendance_pin', 'evv_text'],
    },
];

const OUTREACH_SAMPLE_DETAIL_GROUPS: ReadonlyArray<{
    readonly label: string;
    readonly tokens: ReadonlyArray<string>;
}> = [
    {
        label: 'Caregiver',
        tokens: ['caregiver_first_name', 'caregiver_name'],
    },
    {
        label: 'Shift',
        tokens: [
            'shift_date',
            'shift_day_of_week',
            'shift_duration',
            'shift_start_time',
            'shift_end_time',
            'shift_count',
            'shift_list',
        ],
    },
    {
        label: 'Client',
        tokens: [
            'client_name',
            'client_initials',
            'client_first_initial',
            'client_last_initial',
            'client_first_name',
            'client_last_name',
            'client_city',
            'client_address',
            'client_street_name',
            'client_zip_code',
        ],
    },
    {
        label: 'Care team',
        tokens: [
            'organization_name',
            'care_coordinator',
            'caregiver_distance',
            'previous_visits',
            'care_plan_summary',
        ],
    },
];

const MULTILINE_SAMPLE_FIELDS = new Set([
    'care_plan_summary',
    'client_groups',
    'shift_groups',
    'shift_list',
]);

type OutreachFormValues = {
    readonly payRate?: string;
    readonly openingLine?: string;
    readonly instructions?: string;
    readonly recurrenceScheduleDescription?: string;
    readonly multiShiftHeader?: string;
    readonly multiShiftItem?: string;
};

type DemoCallOutreachType = Exclude<
    OutreachType,
    'message' | 'plan_of_care_duties' | 'shift_confirmation' | 'shift_discovery'
>;

function supportsDemoCall(
    outreachType: OutreachType,
): outreachType is DemoCallOutreachType {
    return (
        outreachType !== 'message' &&
        outreachType !== 'plan_of_care_duties' &&
        outreachType !== 'shift_confirmation' &&
        outreachType !== 'shift_discovery'
    );
}

type PreviewShiftGroup = {
    readonly shiftIds?: ReadonlyArray<string>;
    readonly shift_ids?: ReadonlyArray<string>;
    readonly groupMode?: 'all' | 'any';
    readonly group_mode?: 'all' | 'any';
};

function normalizePreviewShiftGroups(
    shiftGroups: ReadonlyArray<PreviewShiftGroup> | null | undefined,
): Array<
    | {
          readonly shiftIds: Array<string>;
          readonly groupMode: 'all' | 'any';
      }
    | {
          readonly shift_ids: Array<string>;
          readonly group_mode: 'all' | 'any';
      }
> | null {
    if (!shiftGroups || shiftGroups.length === 0) {
        return null;
    }

    return shiftGroups.map((group) =>
        'shiftIds' in group || 'groupMode' in group
            ? {
                  shiftIds: [...(group.shiftIds ?? [])],
                  groupMode: group.groupMode ?? 'all',
              }
            : {
                  shift_ids: [...(group.shift_ids ?? [])],
                  group_mode: group.group_mode ?? 'all',
              },
    );
}

/**
 * Return the one shift group that covers every selected shift, or null.
 *
 * Mirrors dispatch: a group counts only when it still overlaps at least two
 * of the outreach's shifts (each shift counted once, in group order), so
 * stale metadata groups that no longer overlap the selection are ignored.
 */
function getCompleteSingleShiftGroup({
    selectedShiftIds,
    shiftGroups,
}: {
    readonly selectedShiftIds: ReadonlyArray<string>;
    readonly shiftGroups: ReadonlyArray<PreviewShiftGroup> | null | undefined;
}): PreviewShiftGroup | null {
    if (selectedShiftIds.length <= 1 || !shiftGroups?.length) {
        return null;
    }

    const selectedShiftIdSet = new Set(selectedShiftIds);
    const seenShiftIds = new Set<string>();
    let completeGroup: PreviewShiftGroup | null = null;
    let overlappingGroupCount = 0;
    for (const group of shiftGroups) {
        const overlappingShiftIds = new Set(
            [...(group.shiftIds ?? group.shift_ids ?? [])].filter(
                (shiftId) =>
                    selectedShiftIdSet.has(shiftId) &&
                    !seenShiftIds.has(shiftId),
            ),
        );
        if (overlappingShiftIds.size <= 1) {
            continue;
        }
        overlappingGroupCount++;
        completeGroup = group;
        for (const shiftId of overlappingShiftIds) {
            seenShiftIds.add(shiftId);
        }
    }
    return overlappingGroupCount === 1 &&
        seenShiftIds.size === selectedShiftIdSet.size
        ? completeGroup
        : null;
}

/**
 * True when one shift group covers every selected shift and the step still
 * uses the default wrapper and group templates, which is the shape where
 * dispatch renders a single whole-group message instead of a wrapper around
 * group blocks.
 */
function isCompleteDefaultTemplatedGroup({
    outreachType,
    selectedShiftIds,
    shiftGroups,
    step,
}: {
    readonly outreachType: OutreachType;
    readonly selectedShiftIds: ReadonlyArray<string>;
    readonly shiftGroups: ReadonlyArray<PreviewShiftGroup> | null | undefined;
    readonly step: OutreachStep;
}): boolean {
    if (
        getCompleteSingleShiftGroup({ selectedShiftIds, shiftGroups }) === null
    ) {
        return false;
    }

    const defaultWrapper =
        outreachType === 'recurring_shift'
            ? DEFAULT_RECURRING_TEMPLATES.clientTemplate
            : DEFAULT_OPEN_SHIFT_TEMPLATES.clientTemplate;
    // `||`, not `??`: dispatch selects overrides on truthiness, so an empty
    // string falls back to the default template there too. Dispatch accepts
    // only its literal defaults (both group-header defaults count: the
    // shipped recurring template stores RECURRING_SHIFT_PER_GROUP explicitly)
    // — the editor must not qualify any rewrite dispatch would treat as an
    // override.
    const wrapper = step.group_template || defaultWrapper;
    const groupTemplate =
        step.per_group_template || DEFAULT_OPEN_SHIFT_TEMPLATES.groupTemplate;
    return (
        (wrapper === DEFAULT_OPEN_SHIFT_TEMPLATES.clientTemplate ||
            wrapper === DEFAULT_RECURRING_TEMPLATES.clientTemplate) &&
        (groupTemplate === DEFAULT_OPEN_SHIFT_TEMPLATES.groupTemplate ||
            groupTemplate === RECURRING_SHIFT_PER_GROUP_SMS_TEMPLATE)
    );
}

/**
 * True when the selected shifts collapse into one caregiver-visible shift.
 * Dispatch then renders the consolidated-shift template, so that is the only
 * message field an edit here can affect.
 */
function shouldUseConsolidatedShiftTemplate({
    outreachType,
    selectedShifts,
    shiftGroups,
    step,
    timezone,
}: {
    readonly outreachType: OutreachType;
    readonly selectedShifts: ReadonlyArray<Shift>;
    readonly shiftGroups: ReadonlyArray<PreviewShiftGroup> | null | undefined;
    readonly step: OutreachStep;
    readonly timezone: string;
}): boolean {
    if (
        !isCompleteDefaultTemplatedGroup({
            outreachType,
            selectedShiftIds: selectedShifts.map((shift) => shift.id),
            shiftGroups,
            step,
        })
    ) {
        return false;
    }
    return (
        resolveContiguousShiftPreview(
            selectedShifts.map((shift) => ({
                shiftId: shift.id,
                ...shiftToPreviewDataShortDate(shift, timezone),
            })),
        ) !== null
    );
}

function shouldUseCompleteAllShiftsTemplate({
    outreachType,
    selectedShiftIds,
    shiftGroups,
    step,
}: {
    readonly outreachType: OutreachType;
    readonly selectedShiftIds: ReadonlyArray<string>;
    readonly shiftGroups: ReadonlyArray<PreviewShiftGroup> | null | undefined;
    readonly step: OutreachStep;
}): boolean {
    if (outreachType !== 'callout') {
        return false;
    }
    const group = getCompleteSingleShiftGroup({
        selectedShiftIds,
        shiftGroups,
    });
    const groupMode = group?.groupMode ?? group?.group_mode;
    if (groupMode !== 'all') {
        return false;
    }
    return isCompleteDefaultTemplatedGroup({
        outreachType,
        selectedShiftIds,
        shiftGroups,
        step,
    });
}

type PerShiftCarePlans = Record<
    string,
    { readonly summary: string; readonly bullets: string }
>;

type OutreachStepsPreviewProps = {
    readonly steps: Array<OutreachStep>;
    readonly selectedShiftIds: Array<string>;
    readonly selectedShifts: Array<Shift>;
    readonly organization: Organization | undefined;
    readonly outreachType: OutreachType;
    readonly form: UseFormReturn<OutreachFormValues>;
    readonly isLoadingInstructions: boolean;
    readonly onStepsChange?: (steps: Array<OutreachStep>) => void;
    readonly templateSteps?: Array<OutreachStep>;
    readonly perShiftCarePlans?: PerShiftCarePlans;
    // Original outreach steps (preserved even when policy filters them out)
    readonly originalSteps?: Array<OutreachStep>;
    // Custom default opening line from outreach template metadata_schema
    readonly templateOpeningLine?: string | null;
    readonly structuredShiftGroups?: ReadonlyArray<PreviewShiftGroup> | null;
    readonly legacyShiftGroups?: ReadonlySet<string> | null;
};

// Format a single shift line for multi-shift SMS preview
function formatShiftLine(
    shift: Shift,
    organization: Organization | undefined,
    index: number,
): string {
    const timezone = organization?.timezone || 'UTC';
    const shiftDate = formatDate(shift.shift_start_time, timezone);
    const startTime = formatTime(shift.shift_start_time, timezone);
    const endTime = formatTime(shift.shift_end_time, timezone);
    const location = shift.patient_city ? ` in ${shift.patient_city}` : '';
    return `${index}. ${shiftDate}, ${startTime}-${endTime}${location}`;
}

function getPreviewCaregiverFirstName(
    shifts: Array<Shift>,
): string | undefined {
    for (const shift of shifts) {
        const firstName = shift.caregiver?.name?.trim().split(' ')[0];
        if (firstName) {
            return firstName;
        }
    }
    return;
}

function getPreviewCaregiverName(shifts: Array<Shift>): string | undefined {
    for (const shift of shifts) {
        const fullName = shift.caregiver?.name?.trim() || '';
        if (fullName.length > 0) {
            return fullName;
        }
    }
    return;
}

function getTemplateVariableLabel(token: string): string {
    return token.split('_').join(' ');
}

function getEditorVariableGroups(
    variables: ReadonlyArray<TemplateVariable>,
    clientReferenceTerm: ClientReferenceTerm,
): ReadonlyArray<{
    readonly label: string;
    readonly variables: ReadonlyArray<TemplateVariable>;
}> {
    const variableByToken = new Map(
        variables.map((variable) => [variable.token, variable]),
    );
    return OUTREACH_VARIABLE_GROUP_DEFINITIONS.map((group) => ({
        label:
            group.label === 'Client'
                ? `${clientReferenceTerm.charAt(0).toUpperCase()}${clientReferenceTerm.slice(1)}`
                : group.label,
        variables: group.tokens
            .map((token) => variableByToken.get(token))
            .filter(
                (variable): variable is TemplateVariable =>
                    variable !== undefined,
            ),
    })).filter((group) => group.variables.length > 0);
}

function EditorVariableBank({
    variables,
    clientReferenceTerm,
}: {
    readonly variables: ReadonlyArray<TemplateVariable>;
    readonly clientReferenceTerm: ClientReferenceTerm;
}) {
    const groups = useMemo(
        () => getEditorVariableGroups(variables, clientReferenceTerm),
        [clientReferenceTerm, variables],
    );
    return (
        <AvailableVariables
            variables={variables}
            groups={groups.length > 0 ? groups : undefined}
            variableIcons={TEMPLATE_VARIABLE_ICONS}
        />
    );
}

function buildOutreachSampleData({
    selectedShifts,
    organization,
    perShiftCarePlans,
    variables,
}: {
    readonly selectedShifts: Array<Shift>;
    readonly organization: Organization | undefined;
    readonly perShiftCarePlans?: PerShiftCarePlans;
    readonly variables: ReadonlyArray<TemplateVariable>;
}): Record<string, string> {
    const firstSelectedShift = selectedShifts[0] ?? null;
    const overrides: Record<string, string> = {};

    if (firstSelectedShift) {
        const context = buildTemplateContext(
            firstSelectedShift,
            organization,
            getCarePlanSummaryForShift(firstSelectedShift, perShiftCarePlans),
        );
        for (const [token, value] of Object.entries(context)) {
            if (
                typeof value === 'string' &&
                value.trim().length > 0 &&
                !value.startsWith('{')
            ) {
                overrides[token] = value;
            }
        }

        overrides['start_date'] = overrides['shift_date'] ?? '';
    }

    const caregiverName = getPreviewCaregiverName(selectedShifts);
    const caregiverFirstName = getPreviewCaregiverFirstName(selectedShifts);
    if (caregiverName) {
        overrides['caregiver_name'] = caregiverName;
    }
    if (caregiverFirstName) {
        overrides['caregiver_first_name'] = caregiverFirstName;
    }

    if (organization?.name) {
        overrides['organization_name'] = organization.name;
    }

    if (selectedShifts.length > 0) {
        const sortedShifts = sortShiftsByStartTime([...selectedShifts]);
        const shiftLines = sortedShifts.map((shift, index) =>
            formatShiftLine(shift, organization, index + 1),
        );
        overrides['shift_count'] = String(selectedShifts.length);
        overrides['shift_count_number'] = String(selectedShifts.length);
        overrides['shift_list'] = shiftLines.join('\n');

        const clientName =
            overrides['client_name'] ??
            trimPatientNameAnnotations(selectedShifts[0]?.patient_name ?? '');
        const clientCity = overrides['client_city'] ?? '';
        const groupHeader = clientCity
            ? `${clientName} in ${clientCity}:`
            : `${clientName}:`;
        overrides['client_groups'] = `${groupHeader}\n${shiftLines.join('\n')}`;
        overrides['shift_groups'] = `Group 1:\n${shiftLines.join('\n')}`;
    }

    const clientReferenceTerm =
        organization?.general_settings?.client_reference_term;
    if (clientReferenceTerm) {
        overrides['client_reference_term'] = clientReferenceTerm;
    }

    return getTemplateVariablePreviewValues(
        variables.map((variable) => variable.token),
        overrides,
    );
}

function applyOutreachSampleData(
    preview: string,
    sampleData: Readonly<Record<string, string>>,
): string {
    return substituteTemplateVariables(preview, sampleData);
}

function OutreachSampleDetails({
    sampleData,
    onChange,
}: {
    readonly sampleData: Readonly<Record<string, string>>;
    readonly onChange: (token: string, value: string) => void;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const summaryItems = [
        ['Caregiver', sampleData['caregiver_first_name']],
        ['Client', sampleData['client_name']],
        ['Date', sampleData['shift_date']],
        [
            'Time',
            `${sampleData['shift_start_time'] ?? ''} - ${sampleData['shift_end_time'] ?? ''}`,
        ],
    ].filter(
        (item): item is [string, string] =>
            typeof item[1] === 'string' && item[1].trim().length > 0,
    );

    return (
        <div className="rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-medium">Sample details</h3>
                    {!isEditing ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {summaryItems.map(([label, value]) => (
                                <span
                                    key={label}
                                    className="bg-muted text-muted-foreground rounded px-2 py-1 text-xs"
                                >
                                    <span className="font-medium">
                                        {label}:
                                    </span>{' '}
                                    {value}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
                <Button
                    type="button"
                    variant={isEditing ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setIsEditing((current) => !current)}
                    aria-label={
                        isEditing
                            ? 'Done editing sample details'
                            : 'Edit sample details'
                    }
                >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    {isEditing ? 'Done' : 'Edit'}
                </Button>
            </div>
            {isEditing ? (
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                    {OUTREACH_SAMPLE_DETAIL_GROUPS.map((group) => (
                        <div key={group.label} className="space-y-2">
                            <div className="text-muted-foreground text-[11px] leading-none font-medium tracking-wide uppercase">
                                {group.label}
                            </div>
                            <div className="space-y-2">
                                {group.tokens.map((token) => {
                                    const inputId = `outreach_sample_${token}`;
                                    const value = sampleData[token] ?? '';
                                    return (
                                        <div key={token} className="space-y-1">
                                            <Label
                                                htmlFor={inputId}
                                                className="text-xs"
                                            >
                                                {getTemplateVariableLabel(
                                                    token,
                                                )}
                                            </Label>
                                            {MULTILINE_SAMPLE_FIELDS.has(
                                                token,
                                            ) ? (
                                                <Textarea
                                                    id={inputId}
                                                    value={value}
                                                    rows={2}
                                                    onChange={(event) =>
                                                        onChange(
                                                            token,
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <Input
                                                    id={inputId}
                                                    value={value}
                                                    onChange={(event) =>
                                                        onChange(
                                                            token,
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

// Generate multi-shift SMS preview with numbered list
// Uses org templates if available, otherwise falls back to hardcoded format
function generateMultiShiftSmsPreview(
    shifts: Array<Shift>,
    organization: Organization | undefined,
    outreachType: OutreachType,
    openShiftTemplates?: OpenShiftTemplates,
    step?: OutreachStep,
    perShiftCarePlans?: Record<string, { summary: string; bullets: string }>,
    shiftGroupModesByKey?: ReadonlyMap<string, ShiftGroupMode>,
    structuredShiftGroups?: ReadonlyArray<PreviewShiftGroup> | null,
): string {
    const timezone = organization?.timezone || 'UTC';
    const caregiverFirstName = getPreviewCaregiverFirstName(shifts);
    const caregiverName = getPreviewCaregiverName(shifts);
    const clientReferenceTerm =
        organization?.general_settings?.client_reference_term ?? 'client';
    const hasShiftGroups =
        (structuredShiftGroups?.length ?? 0) > 0 ||
        (shiftGroupModesByKey?.size ?? 0) > 0;
    const defaults =
        outreachType === 'recurring_shift'
            ? DEFAULT_RECURRING_TEMPLATES
            : DEFAULT_OPEN_SHIFT_TEMPLATES;
    const templates = {
        clientTemplate:
            (hasShiftGroups
                ? step?.group_template
                : openShiftTemplates?.multiHeader) || defaults.clientTemplate,
        // `||`, not `??`: dispatch selects overrides on truthiness, so an
        // empty string falls back to the default template there too.
        groupTemplate: step?.per_group_template || defaults.groupTemplate,
        itemTemplate:
            step?.multi_shift_item_template ||
            openShiftTemplates?.multiItem ||
            defaults.itemTemplate,
        singleClientTemplate: step?.single_client_template ?? null,
        allShiftsTemplate:
            step?.callout_multi_shift_all_template ??
            defaults.allShiftsTemplate,
        consolidatedShiftTemplate:
            step?.consolidated_shift_template ??
            defaults.consolidatedShiftTemplate,
    };
    const shiftPreviewData = shifts.map((shift) => ({
        shiftId: shift.id,
        ...shiftToPreviewDataShortDate(shift, timezone),
        carePlanSummary: getCarePlanSummaryForShift(shift, perShiftCarePlans),
    }));
    return generateGroupedMultiShiftSmsPreview(
        templates,
        shiftPreviewData,
        {
            caregiverFirstName,
            caregiverName,
            organizationName: organization?.name,
            careCoordinator: getCareCoordinatorNameFromShifts(shifts),
            clientReferenceTerm,
        },
        shiftGroupModesByKey,
        normalizePreviewShiftGroups(structuredShiftGroups),
        outreachType,
    );
}

// Generate SMS preview from step, shift, and organization
// Uses step's callout_template (or legacy message_template) if available.
function generateSmsPreview(
    step: OutreachStep,
    selectedShift: Shift | null | undefined,
    organization: Organization | undefined,
    carePlanSummary?: string,
): string {
    const template = step.callout_template ?? step.message_template;
    if (typeof template !== 'string' || template.trim().length === 0) {
        return '';
    }

    if (!selectedShift) {
        return template;
    }

    const context = buildTemplateContext(
        selectedShift,
        organization,
        carePlanSummary,
    );
    return substituteTemplateVariables(template, context);
}

// Generate default opening line based on outreach type and shifts
export function generateDefaultOpeningLine(
    outreachType: OutreachType,
    selectedShifts: Array<Shift>,
    organization?: Organization,
): string {
    return getDefaultVoiceOpeningLine({
        outreachType,
        isMultiShift: selectedShifts.length > 1,
        supportsShiftRecurrence:
            organizationSupportsShiftRecurrence(organization),
    });
}

// Generate voice preview with organization name and opening line
function generateVoicePreview(
    organization: Organization | undefined,
    openingLine: string,
    phoebeIntro?: string | null,
    selectedShift?: Shift | null,
): string {
    const orgName = organization?.name || '{organization_name}';
    const trimmedPhoebeIntro = phoebeIntro?.trim() || '';
    const intro = trimmedPhoebeIntro || `it's Phoebe with ${orgName}`;
    const customIntroProvided = trimmedPhoebeIntro.length > 0;

    let renderedIntro = substituteTemplateVariables(
        intro,
        withTemplateVariableAliases({
            caregiver_first_name: '{caregiver_first_name}',
            caregiver_name: '{caregiver_name}',
            organization_name: orgName,
        }),
    );
    let renderedOpeningLine = openingLine;
    if (selectedShift) {
        const context = buildVoiceTemplateContext(selectedShift, organization);
        renderedIntro = substituteTemplateVariables(intro, context);
        renderedOpeningLine = substituteTemplateVariables(openingLine, context);
    }

    const initialGreeting = customIntroProvided
        ? renderedIntro
        : `Hi {caregiver_first_name}, ${renderedIntro}`;

    return `${initialGreeting}. ${renderedOpeningLine}`;
}

function getCarePlanSummaryForShift(
    shift: Shift | null | undefined,
    perShiftCarePlans?: PerShiftCarePlans,
): string | undefined {
    if (!shift) {
        return undefined;
    }
    return resolveCarePlanSummary(perShiftCarePlans, shift.id) || undefined;
}

function getStepPreviewTitle(step: OutreachStep): string {
    return step.step_type === 'voice' ? 'Voice Opening Line' : step.step_type;
}

function EmptyStepsPreview() {
    return (
        <div className="space-y-2">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                Contact Preview
            </h3>
            <p className="text-muted-foreground text-sm">
                This template does not have steps configured yet.
            </p>
        </div>
    );
}

// Extract OpenShiftTemplates from an array of OutreachSteps
function extractOpenShiftTemplatesFromSteps(
    steps: Array<OutreachStep> | undefined,
): OpenShiftTemplates | undefined {
    if (!steps) {
        return undefined;
    }
    const smsStep = steps.find((s) => s.step_type === 'sms');
    if (!smsStep) {
        return undefined;
    }
    const single = smsStep.callout_template ?? '';
    const multiHeader = smsStep.callout_multi_shift_template ?? '';
    const multiItem = smsStep.multi_shift_item_template ?? '';
    return { single, multiHeader, multiItem };
}

function getFirstStepOfType(
    steps: ReadonlyArray<OutreachStep> | undefined,
    stepType: OutreachStep['step_type'],
): OutreachStep | undefined {
    return steps?.find((step) => step.step_type === stepType);
}

/**
 * Merges each step with the matching template step, using template
 * values as fallbacks for null fields. Use this to produce complete
 * steps before passing them to `computeSmsStepPreviews`.
 */
export function mergeStepsWithTemplateFallbacks(
    steps: ReadonlyArray<OutreachStep>,
    templateSteps: ReadonlyArray<OutreachStep> | undefined,
): Array<OutreachStep> {
    return steps.map((step) =>
        mergeStepWithTemplateFallback(
            step,
            getFirstStepOfType(templateSteps, step.step_type),
        ),
    );
}

function mergeStepWithTemplateFallback(
    step: OutreachStep,
    templateStep: OutreachStep | undefined,
): OutreachStep {
    if (!templateStep) {
        return step;
    }

    return {
        ...templateStep,
        ...step,
        message_template:
            step.message_template ?? templateStep.message_template,
        prompt_alteration:
            step.prompt_alteration ?? templateStep.prompt_alteration,
        multi_shift_prompt_alteration:
            step.multi_shift_prompt_alteration ??
            templateStep.multi_shift_prompt_alteration,
        callout_template:
            step.callout_template ?? templateStep.callout_template,
        callout_multi_shift_template:
            step.callout_multi_shift_template ??
            templateStep.callout_multi_shift_template,
        callout_multi_shift_all_template:
            step.callout_multi_shift_all_template ??
            templateStep.callout_multi_shift_all_template,
        consolidated_shift_template:
            step.consolidated_shift_template ??
            templateStep.consolidated_shift_template,
        multi_shift_item_template:
            step.multi_shift_item_template ??
            templateStep.multi_shift_item_template,
        group_template: step.group_template ?? templateStep.group_template,
        per_group_template:
            step.per_group_template ?? templateStep.per_group_template,
        single_client_template:
            step.single_client_template ?? templateStep.single_client_template,
    };
}

/**
 * Renders SMS previews for all steps in an outreach, index-aligned with the
 * input `steps` array. Non-SMS steps return an empty string. This mirrors the
 * logic used internally by OutreachStepsPreview so that external callers
 * (submit-button guards in the create outreach modal, outreach detail page,
 * and edit steps modal) can validate preview length without rendering the
 * preview component.
 */
export function computeSmsStepPreviews({
    steps,
    selectedShifts,
    organization,
    outreachType,
    perShiftCarePlans,
    structuredShiftGroups,
    legacyShiftGroups,
}: {
    readonly steps: ReadonlyArray<OutreachStep>;
    readonly selectedShifts: Array<Shift>;
    readonly organization: Organization | undefined;
    readonly outreachType: OutreachType;
    readonly perShiftCarePlans?: PerShiftCarePlans;
    readonly structuredShiftGroups?: ReadonlyArray<PreviewShiftGroup> | null;
    readonly legacyShiftGroups?: ReadonlySet<string> | null;
}): Array<string> {
    const firstSelectedShift = selectedShifts[0] ?? null;
    const isMultiShift = selectedShifts.length > 1;
    const shiftGroupModesByKey = resolveShiftGroupModesByKey({
        shifts: selectedShifts,
        shiftGroups: normalizePreviewShiftGroups(structuredShiftGroups),
        legacyShiftGroupKeys: legacyShiftGroups,
    });

    return steps.map((step) => {
        if (step.step_type !== 'sms') {
            return '';
        }
        if (isMultiShift) {
            const openShiftTemplates =
                outreachType === 'recurring_shift'
                    ? undefined
                    : extractOpenShiftTemplatesFromSteps([step]);
            return generateMultiShiftSmsPreview(
                selectedShifts,
                organization,
                outreachType,
                openShiftTemplates,
                step,
                perShiftCarePlans,
                shiftGroupModesByKey,
                structuredShiftGroups,
            );
        }
        return generateSmsPreview(
            step,
            firstSelectedShift,
            organization,
            getCarePlanSummaryForShift(firstSelectedShift, perShiftCarePlans),
        );
    });
}

function getSingleShiftSmsField(
    step: OutreachStep,
    outreachType: OutreachType,
) {
    if (outreachType === 'recurring_shift') {
        return 'message_template' as const;
    }
    return 'callout_template' as const;
}

function getSingleShiftSmsTemplate(
    step: OutreachStep,
    outreachType: OutreachType,
): string {
    if (outreachType === 'recurring_shift') {
        return step.message_template ?? '';
    }

    return step.callout_template ?? step.message_template ?? '';
}

function getSingleShiftSmsEditorValue(
    step: OutreachStep,
    outreachType: OutreachType,
    {
        selectedShift,
        organization,
        carePlanSummary,
    }: {
        readonly selectedShift: Shift | null | undefined;
        readonly organization: Organization | undefined;
        readonly carePlanSummary: string | undefined;
    },
): string {
    const template = getSingleShiftSmsTemplate(step, outreachType);
    if (!selectedShift) {
        return template;
    }

    return substituteTemplateVariables(
        template,
        buildTemplateContext(selectedShift, organization, carePlanSummary),
    );
}

function getMultiShiftHeaderEditorValue(
    step: OutreachStep,
    usesGroupedTemplates: boolean,
    usesSingleClientTemplate: boolean,
    usesCompleteAllShiftsTemplate: boolean,
    usesConsolidatedShiftTemplate: boolean,
    outreachType: OutreachType,
): string {
    // `||`, not `??`: dispatch selects overrides on truthiness, so an empty
    // string falls back to the default template there too.
    const template = usesConsolidatedShiftTemplate
        ? step.consolidated_shift_template ||
          (outreachType === 'recurring_shift'
              ? RECURRING_CONSOLIDATED_SHIFT_SMS_TEMPLATE
              : CONSOLIDATED_SHIFT_SMS_TEMPLATE)
        : usesCompleteAllShiftsTemplate
          ? step.callout_multi_shift_all_template ||
            MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE
          : usesSingleClientTemplate
            ? (step.single_client_template ?? '')
            : usesGroupedTemplates
              ? (step.group_template ?? '')
              : (step.callout_multi_shift_template ??
                step.callout_template ??
                step.message_template ??
                '');
    return template;
}

function getSingleClientTemplateKey(shift: Shift): string | null {
    const clientId = shift.client_id?.trim();
    if (clientId) {
        return clientId;
    }
    // Mirrors `shift_client_key` in
    // `libraries/python/outreaches/shift_group_utils.py`: the name fallback is
    // casefolded so differently cased names still group as one client.
    // toLowerCase, not toLocaleLowerCase: Python's casefold() is
    // locale-independent, and a locale-sensitive fold (e.g. Turkish dotless I)
    // could pick a different branch than dispatch.
    const patientName = (shift.patient_name ?? '').trim();
    if (!patientName) {
        return null;
    }
    return trimPatientNameAnnotations(patientName).toLowerCase();
}

function shouldUseSingleClientTemplateEditor({
    step,
    selectedShifts,
    usesGroupedTemplates,
}: {
    readonly step: OutreachStep;
    readonly selectedShifts: Array<Shift>;
    readonly usesGroupedTemplates: boolean;
}): boolean {
    // Truthiness, not trim: dispatch selects the single-client branch on the
    // raw value, so a whitespace-only template still counts as set there.
    if (
        !usesGroupedTemplates ||
        selectedShifts.length <= 1 ||
        !step.single_client_template
    ) {
        return false;
    }

    const clientKeys = new Set(
        selectedShifts.map((shift) => getSingleClientTemplateKey(shift)),
    );
    return clientKeys.size === 1 && !clientKeys.has(null);
}

function getVoiceStepEditorValue(
    step: OutreachStep,
    {
        outreachType,
        selectedShifts,
        templateOpeningLine,
        isPrimaryVoiceStep,
        organization,
        selectedShift,
    }: {
        readonly outreachType: OutreachType;
        readonly selectedShifts: Array<Shift>;
        readonly templateOpeningLine?: string | null;
        readonly isPrimaryVoiceStep: boolean;
        readonly organization: Organization | undefined;
        readonly selectedShift: Shift | null | undefined;
    },
): string {
    const populatedTemplateContext = selectedShift
        ? {
              ...buildVoiceTemplateContext(selectedShift, organization),
              shift_count: String(selectedShifts.length),
          }
        : null;
    const currentOpeningLine = getStepVoiceOpeningLineValue({
        step,
        outreachType,
        isMultiShift: selectedShifts.length > 1,
    });
    if (currentOpeningLine !== null && currentOpeningLine !== undefined) {
        if (!populatedTemplateContext) {
            return currentOpeningLine;
        }
        return substituteTemplateVariablesExceptZipcode(
            currentOpeningLine,
            populatedTemplateContext,
        );
    }

    if (isPrimaryVoiceStep && templateOpeningLine?.trim()) {
        const templateEditorValue = templateOpeningLine;
        if (!populatedTemplateContext) {
            return templateEditorValue;
        }
        return substituteTemplateVariablesExceptZipcode(templateEditorValue, {
            ...populatedTemplateContext,
        });
    }

    const defaultEditorValue = generateDefaultOpeningLine(
        outreachType,
        selectedShifts,
        organization,
    );
    if (!populatedTemplateContext) {
        return defaultEditorValue;
    }
    return substituteTemplateVariablesExceptZipcode(defaultEditorValue, {
        ...populatedTemplateContext,
    });
}

type BaseSmsStepProps = {
    readonly variables: ReadonlyArray<TemplateVariable>;
    readonly smsPreview: string;
    readonly value: string;
    readonly onChange: (value: string) => void;
    readonly label?: string;
    readonly placeholder: string;
    readonly required?: boolean;
    readonly variablePreviewValues: Readonly<Record<string, string>>;
    readonly clientReferenceTerm: ClientReferenceTerm;
};

function BaseSmsStep({
    variables,
    smsPreview,
    value,
    onChange,
    label,
    placeholder,
    required = false,
    variablePreviewValues,
    clientReferenceTerm,
}: BaseSmsStepProps) {
    return (
        <div className="space-y-3">
            <div className="bg-muted/30 text-muted-foreground rounded p-1 text-sm whitespace-pre-wrap">
                <VariableTextDisplay value={smsPreview} variables={variables} />
            </div>
            <FormItem>
                {label && (
                    <Label>
                        {label}
                        {required && (
                            <span className="text-destructive">*</span>
                        )}
                    </Label>
                )}
                <VariableTextEditor
                    value={value}
                    onChange={onChange}
                    variables={variables}
                    placeholder={placeholder}
                    ariaLabel={label ?? 'SMS message'}
                    variablePreviewValues={variablePreviewValues}
                    variableIcons={TEMPLATE_VARIABLE_ICONS}
                />
                <div className="mt-2">
                    <EditorVariableBank
                        variables={variables}
                        clientReferenceTerm={clientReferenceTerm}
                    />
                </div>
            </FormItem>
        </div>
    );
}

type MultiShiftSmsStepProps = {
    readonly headerVariables: ReadonlyArray<TemplateVariable>;
    readonly itemVariables: ReadonlyArray<TemplateVariable>;
    readonly smsPreview: string;
    readonly headerValue: string;
    readonly onHeaderChange: (value: string) => void;
    readonly itemValue: string;
    readonly onItemChange: (value: string) => void;
    readonly variablePreviewValues: Readonly<Record<string, string>>;
    readonly clientReferenceTerm: ClientReferenceTerm;
};

function MultiShiftSmsStep({
    headerVariables,
    itemVariables,
    smsPreview,
    headerValue,
    onHeaderChange,
    itemValue,
    onItemChange,
    variablePreviewValues,
    clientReferenceTerm,
}: MultiShiftSmsStepProps) {
    return (
        <div className="space-y-3">
            <div className="bg-muted/30 text-muted-foreground rounded p-1 text-sm whitespace-pre-wrap">
                <VariableTextDisplay
                    value={smsPreview}
                    variables={[...headerVariables, ...itemVariables]}
                />
            </div>
            <div className="space-y-4">
                <FormItem>
                    <Label className="text-xs">Message Template</Label>
                    <VariableTextEditor
                        value={headerValue}
                        onChange={onHeaderChange}
                        variables={headerVariables}
                        placeholder="e.g., we have {shift_count} open shifts:\n\n{shift_list}\n\nAre you available?"
                        ariaLabel="Multi-shift message template"
                        variablePreviewValues={variablePreviewValues}
                        variableIcons={TEMPLATE_VARIABLE_ICONS}
                    />
                    <div className="mt-2">
                        <EditorVariableBank
                            variables={headerVariables}
                            clientReferenceTerm={clientReferenceTerm}
                        />
                    </div>
                </FormItem>
                <FormItem>
                    <Label className="text-xs">Shift Format</Label>
                    <VariableTextEditor
                        value={itemValue}
                        onChange={onItemChange}
                        variables={itemVariables}
                        placeholder="e.g., {shift_date}, {shift_start_time}-{shift_end_time}{location_text}\n{care_plan_summary}"
                        ariaLabel="Multi-shift item format"
                        variablePreviewValues={variablePreviewValues}
                        variableIcons={TEMPLATE_VARIABLE_ICONS}
                    />
                    <div className="mt-2">
                        <EditorVariableBank
                            variables={itemVariables}
                            clientReferenceTerm={clientReferenceTerm}
                        />
                    </div>
                </FormItem>
            </div>
        </div>
    );
}

type ShiftClientGroupsProps = {
    readonly shifts: Array<Shift>;
    readonly organization: Organization | undefined;
    readonly shiftGroups: ReadonlySet<string>;
    readonly onToggleGroup: (groupKey: string) => void;
};

export function ShiftClientGroups({
    shifts,
    organization,
    shiftGroups,
    onToggleGroup,
}: ShiftClientGroupsProps) {
    const timezone = organization?.timezone || 'UTC';

    const groups = useMemo(() => {
        const grouped = new Map<
            string,
            {
                readonly name: string;
                readonly shifts: Array<Shift>;
            }
        >();
        for (const shift of shifts) {
            const key =
                shift.client_id ||
                (shift.patient_name || '').trim() ||
                'Unknown';
            const existing = grouped.get(key);
            if (existing) {
                existing.shifts.push(shift);
            } else {
                grouped.set(key, {
                    name: (shift.patient_name || '').trim() || 'Unknown',
                    shifts: [shift],
                });
            }
        }
        return grouped;
    }, [shifts]);

    return (
        <div className="space-y-4">
            {[...groups.entries()].map(
                ([groupKey, { name: patientName, shifts: groupShifts }]) => (
                    <div key={groupKey} className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                                {patientName}
                                <span className="text-muted-foreground ml-1 font-normal">
                                    ({groupShifts.length}{' '}
                                    {groupShifts.length === 1
                                        ? 'shift'
                                        : 'shifts'}
                                    )
                                </span>
                            </span>
                            {groupShifts.length > 1 && (
                                <label className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                        className="border-foreground/50 border-[2px]"
                                        checked={shiftGroups.has(groupKey)}
                                        onCheckedChange={() =>
                                            onToggleGroup(groupKey)
                                        }
                                    />
                                    All shifts must be accepted together
                                </label>
                            )}
                        </div>
                        <div className="rounded-lg border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date / Time</TableHead>
                                        <TableHead>Location</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortShiftsByStartTime(groupShifts).map(
                                        (shift) => (
                                            <TableRow key={shift.id}>
                                                <TableCell>
                                                    {formatDateAndTimeWithWeekday(
                                                        shift.shift_start_time,
                                                        timezone,
                                                    )}{' '}
                                                    -{' '}
                                                    {formatTime(
                                                        shift.shift_end_time,
                                                        timezone,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {shift.patient_city ||
                                                        '\u2014'}
                                                </TableCell>
                                            </TableRow>
                                        ),
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                ),
            )}
        </div>
    );
}

type BaseVoiceStepProps = {
    readonly variables: ReadonlyArray<TemplateVariable>;
    readonly voicePreview: string;
    readonly openingLineValue: string;
    readonly onOpeningLineChange: (value: string) => void;
    readonly control: Control<OutreachFormValues>;
    readonly watch: UseFormWatch<OutreachFormValues>;
    readonly selectedShiftIds: Array<string>;
    readonly isLoadingInstructions: boolean;
    readonly outreachType: OutreachType;
    readonly phoebeIntro?: string | null;
    readonly showRecurrenceScheduleInput?: boolean;
    readonly structuredShiftGroups?: ReadonlyArray<PreviewShiftGroup> | null;
    readonly variablePreviewValues: Readonly<Record<string, string>>;
    readonly clientReferenceTerm: ClientReferenceTerm;
};

function BaseVoiceStep({
    variables,
    voicePreview,
    openingLineValue,
    onOpeningLineChange,
    control,
    watch,
    selectedShiftIds,
    isLoadingInstructions,
    outreachType,
    phoebeIntro,
    showRecurrenceScheduleInput = false,
    structuredShiftGroups,
    variablePreviewValues,
    clientReferenceTerm,
}: BaseVoiceStepProps) {
    const shouldShowDemoCallButton = supportsDemoCall(outreachType);

    return (
        <div className="space-y-3">
            <div className="bg-muted/30 text-muted-foreground rounded p-1 text-sm">
                <VariableTextDisplay
                    value={voicePreview}
                    variables={variables}
                />
            </div>
            <FormItem>
                <VariableTextEditor
                    value={openingLineValue}
                    onChange={onOpeningLineChange}
                    variables={variables}
                    placeholder="Type here to change what Phoebe says after their greeting"
                    ariaLabel="Voice opening line"
                    variablePreviewValues={variablePreviewValues}
                    variableIcons={TEMPLATE_VARIABLE_ICONS}
                />
                <div className="mt-2">
                    <EditorVariableBank
                        variables={variables}
                        clientReferenceTerm={clientReferenceTerm}
                    />
                </div>
            </FormItem>
            {showRecurrenceScheduleInput && (
                <FormField
                    control={control}
                    name="recurrenceScheduleDescription"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                Recurrence Schedule
                                <span className="text-destructive">*</span>
                            </FormLabel>
                            <FormControl>
                                <Textarea
                                    placeholder="Describe the recurrence schedule to the voice agent (e.g. Every Monday, Wednesday, and Friday)"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            )}
            {shouldShowDemoCallButton ? (
                <DemoCallButton
                    selectedShiftIds={selectedShiftIds}
                    instructions={watch('instructions')}
                    openingLine={openingLineValue}
                    payRate={watch('payRate')}
                    recurrenceScheduleDescription={watch(
                        'recurrenceScheduleDescription',
                    )}
                    phoebeIntro={phoebeIntro || undefined}
                    isDisabled={isLoadingInstructions}
                    agentType={outreachType}
                    shiftGroups={
                        structuredShiftGroups
                            ?.filter(
                                (g) =>
                                    (g.shift_ids ?? g.shiftIds) !== undefined,
                            )
                            .map((g) => ({
                                shift_ids: [
                                    ...(g.shift_ids ?? g.shiftIds ?? []),
                                ],
                                group_mode:
                                    g.group_mode ?? g.groupMode ?? 'all',
                            })) ?? undefined
                    }
                />
            ) : null}
        </div>
    );
}

export function OutreachStepsPreview({
    steps,
    selectedShiftIds,
    selectedShifts,
    organization,
    outreachType,
    form,
    isLoadingInstructions,
    onStepsChange,
    templateSteps,
    perShiftCarePlans,
    originalSteps,
    templateOpeningLine,
    structuredShiftGroups,
    legacyShiftGroups,
}: OutreachStepsPreviewProps) {
    const { data: voiceSettings } = useOrganizationVoiceSettings(
        organization?.id || null,
    );
    const { data: variableManifest } = useTemplateVariableManifest(
        organization?.id || null,
        {
            reason: 'outreach-step-template-variables',
        },
    );
    const clientReferenceTerm =
        variableManifest?.client_reference_term ?? 'client';
    const { confirmAddingStep, spammyFollowUpConfirmationDialog } =
        useSpammyFollowUpConfirmation();
    const supportsShiftRecurrence =
        organizationSupportsShiftRecurrence(organization);
    const variableFlows = useMemo(
        () => getOutreachTemplateVariableGroups(variableManifest, outreachType),
        [outreachType, variableManifest],
    );
    const outreachVariables = useMemo(
        () => mergeTemplateVariables(...Object.values(variableFlows)),
        [variableFlows],
    );

    // Derive control and watch from form for internal components
    const control = form.control;
    const watch = form.watch;
    // Extract stable form methods to avoid including form object in useEffect deps
    const { getFieldState, setValue } = form;

    const visibleSteps = useMemo(
        () => getVisibleOutreachSteps(steps, templateSteps, outreachType),
        [steps, templateSteps, outreachType],
    );
    const firstSelectedShift = selectedShifts[0] ?? null;
    const isMultiShift = selectedShifts.length > 1;
    const firstVoiceStepIndex = useMemo(
        () => visibleSteps.findIndex((step) => step.step_type === 'voice'),
        [visibleSteps],
    );
    const canEditStepStructure =
        Boolean(onStepsChange) &&
        (outreachType === 'callout' || outreachType === 'recurring_shift');
    const shiftGroupModesByKey = useMemo(
        () =>
            resolveShiftGroupModesByKey({
                shifts: selectedShifts,
                shiftGroups: normalizePreviewShiftGroups(structuredShiftGroups),
                legacyShiftGroupKeys: legacyShiftGroups,
            }),
        [legacyShiftGroups, selectedShifts, structuredShiftGroups],
    );
    const defaultSampleData = useMemo(
        () =>
            buildOutreachSampleData({
                selectedShifts,
                organization,
                perShiftCarePlans,
                variables: outreachVariables,
            }),
        [organization, outreachVariables, perShiftCarePlans, selectedShifts],
    );
    const [customSampleData, setCustomSampleData] = useState<
        Record<string, string>
    >({});
    const sampleData = useMemo(
        () =>
            withTemplateVariableAliases({
                ...defaultSampleData,
                ...customSampleData,
            }),
        [customSampleData, defaultSampleData],
    );
    const handleSampleDataChange = useCallback(
        (token: string, value: string) => {
            setCustomSampleData((current) => ({
                ...current,
                [token]: value,
            }));
        },
        [],
    );

    const recurrenceScheduleSourceKey = JSON.stringify(
        selectedShifts.map((shift) => ({
            id: shift.id,
            start: shift.shift_start_time,
            end: shift.shift_end_time,
            frequency: shift.frequency,
            interval: shift.recurrence_interval,
            clientId: shift.client_id,
            patientName: shift.patient_name,
            patientCity: shift.patient_city,
        })),
    );
    const lastAutoGeneratedRecurrence = useRef<string | null>(null);

    // Pre-fill recurrence schedule description for recurring shifts
    useEffect(() => {
        if (outreachType === 'recurring_shift' && selectedShifts.length > 0) {
            const recurrenceState = getFieldState(
                'recurrenceScheduleDescription',
            );
            const currentRecurrenceValue = watch(
                'recurrenceScheduleDescription',
            );
            const canAutoFill =
                !recurrenceState.isDirty &&
                (!currentRecurrenceValue ||
                    currentRecurrenceValue ===
                        lastAutoGeneratedRecurrence.current);
            // Preserve saved or user-edited descriptions, but refresh a value
            // that this component generated when recurrence metadata changes.
            if (canAutoFill) {
                const timezone = organization?.timezone || 'UTC';
                let description: string;
                if (selectedShifts.length === 1) {
                    description = generateRecurrenceDescription(
                        selectedShifts,
                        timezone,
                    );
                } else {
                    // Group by client ID for multi-shift
                    const grouped = new Map<
                        string,
                        { readonly name: string; readonly shifts: Array<Shift> }
                    >();
                    for (const shift of selectedShifts) {
                        const key =
                            shift.client_id ||
                            (shift.patient_name || '').trim() ||
                            'Unknown';
                        const existing = grouped.get(key);
                        if (existing) {
                            existing.shifts.push(shift);
                        } else {
                            grouped.set(key, {
                                name:
                                    (shift.patient_name || '').trim() ||
                                    'Unknown',
                                shifts: [shift],
                            });
                        }
                    }

                    let globalIndex = 0;
                    const blocks: Array<string> = [];

                    for (const [
                        groupKey,
                        { name: patientName, shifts: groupShifts },
                    ] of grouped) {
                        const city = groupShifts[0]?.patient_city || '';
                        const header = city
                            ? `${patientName} in ${city}:`
                            : `${patientName}:`;
                        const lines = groupShifts.map((shift) => {
                            globalIndex++;
                            const perShift = generateRecurrenceDescription(
                                [shift],
                                timezone,
                            );
                            return `${globalIndex}. ${perShift}`;
                        });
                        const tag =
                            shiftGroupModesByKey.get(groupKey) === 'all' &&
                            groupShifts.length > 1
                                ? ' (all shifts must be accepted together)'
                                : '';
                        const block = `${header}${tag}\n${lines.join('\n')}`;
                        blocks.push(block);
                    }
                    description = blocks.join('\n\n');
                }
                setValue('recurrenceScheduleDescription', description, {
                    shouldDirty: false,
                });
                lastAutoGeneratedRecurrence.current = description;
            }
        }
        // The serialized source key changes when schedule metadata changes
        // without rerunning merely because the shifts array has a new identity.
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [
        recurrenceScheduleSourceKey,
        outreachType,
        getFieldState,
        setValue,
        organization,
        shiftGroupModesByKey,
    ]);

    const handleDelayChange = useCallback(
        (stepIndex: number, delayMinutes: number) => {
            if (!onStepsChange) {
                return;
            }
            const newSteps = visibleSteps.map((step, index) =>
                index === stepIndex
                    ? { ...step, delay_in_minutes: delayMinutes }
                    : step,
            );
            onStepsChange(newSteps);
        },
        [onStepsChange, visibleSteps],
    );

    const handleAddStep = useCallback(
        async (stepType: OutreachStep['step_type']) => {
            if (!onStepsChange) {
                return;
            }

            // Confirm at the moment of adding: block the add when this step
            // would push the sequence past the recommended message count.
            if (!(await confirmAddingStep(visibleSteps.length + 1))) {
                return;
            }

            onStepsChange(
                appendOutreachStep({
                    stepType,
                    steps: visibleSteps,
                    originalSteps,
                    templateSteps,
                    outreachType,
                    supportsShiftRecurrence,
                }),
            );
        },
        [
            confirmAddingStep,
            onStepsChange,
            originalSteps,
            outreachType,
            supportsShiftRecurrence,
            templateSteps,
            visibleSteps,
        ],
    );

    const handleRemoveStep = useCallback(
        (stepIndex: number) => {
            if (!onStepsChange) {
                return;
            }

            onStepsChange(
                removeOutreachStep(visibleSteps, stepIndex, outreachType),
            );
        },
        [onStepsChange, outreachType, visibleSteps],
    );

    const usesGroupedMultiShiftSms =
        (structuredShiftGroups?.length ?? 0) > 0 ||
        shiftGroupModesByKey.size > 0 ||
        (outreachType === 'recurring_shift' && isMultiShift);

    const updateStep = useCallback(
        (stepIndex: number, patch: Partial<OutreachStep>) => {
            if (!onStepsChange) {
                return;
            }

            onStepsChange(
                visibleSteps.map((step, index) =>
                    index === stepIndex ? { ...step, ...patch } : step,
                ),
            );
        },
        [onStepsChange, visibleSteps],
    );
    const getEffectiveStep = useCallback(
        (step: OutreachStep) =>
            mergeStepWithTemplateFallback(
                step,
                getFirstStepOfType(templateSteps, step.step_type),
            ),
        [templateSteps],
    );

    const handleSingleShiftSmsChange = useCallback(
        (stepIndex: number, value: string) => {
            const step = visibleSteps[stepIndex];
            if (!step || step.step_type !== 'sms') {
                return;
            }

            const field = getSingleShiftSmsField(step, outreachType);
            updateStep(stepIndex, {
                [field]: value,
            });
        },
        [outreachType, updateStep, visibleSteps],
    );

    const handleMultiShiftHeaderChange = useCallback(
        (
            stepIndex: number,
            value: string,
            usesCompleteAllShiftsTemplate: boolean,
            usesConsolidatedShiftTemplate: boolean,
        ) => {
            const step = visibleSteps[stepIndex];
            if (!step || step.step_type !== 'sms') {
                return;
            }
            const effectiveStep = getEffectiveStep(step);
            const usesSingleClientTemplate =
                shouldUseSingleClientTemplateEditor({
                    step: effectiveStep,
                    selectedShifts,
                    usesGroupedTemplates: usesGroupedMultiShiftSms,
                });

            updateStep(stepIndex, {
                ...(usesConsolidatedShiftTemplate
                    ? { consolidated_shift_template: value }
                    : usesCompleteAllShiftsTemplate
                      ? { callout_multi_shift_all_template: value }
                      : usesSingleClientTemplate
                        ? { single_client_template: value }
                        : usesGroupedMultiShiftSms
                          ? { group_template: value }
                          : { callout_multi_shift_template: value }),
            });
        },
        [
            getEffectiveStep,
            selectedShifts,
            updateStep,
            usesGroupedMultiShiftSms,
            visibleSteps,
        ],
    );

    const handleMultiShiftItemChange = useCallback(
        (stepIndex: number, value: string) => {
            updateStep(stepIndex, {
                multi_shift_item_template: value,
            });
        },
        [updateStep],
    );

    const handleVoiceOpeningLineChange = useCallback(
        (stepIndex: number, value: string) => {
            updateStep(stepIndex, {
                ...((outreachType === 'callout' ||
                    outreachType === 'recurring_shift') &&
                isMultiShift
                    ? { multi_shift_prompt_alteration: value }
                    : { prompt_alteration: value }),
            });
        },
        [isMultiShift, outreachType, updateStep],
    );

    const smsStepPreviews = useMemo(
        () =>
            computeSmsStepPreviews({
                steps: visibleSteps.map(getEffectiveStep),
                selectedShifts,
                organization,
                outreachType,
                perShiftCarePlans,
                structuredShiftGroups,
                legacyShiftGroups,
            }),
        [
            visibleSteps,
            getEffectiveStep,
            selectedShifts,
            organization,
            outreachType,
            perShiftCarePlans,
            structuredShiftGroups,
            legacyShiftGroups,
        ],
    );

    const voicePreview = useCallback(
        (step: OutreachStep, index: number) =>
            generateVoicePreview(
                organization,
                getVoiceStepEditorValue(step, {
                    outreachType,
                    selectedShifts,
                    templateOpeningLine,
                    isPrimaryVoiceStep: index === firstVoiceStepIndex,
                    organization,
                    selectedShift: firstSelectedShift,
                }),
                voiceSettings?.phoebe_intro,
                firstSelectedShift,
            ),
        [
            firstSelectedShift,
            firstVoiceStepIndex,
            organization,
            outreachType,
            selectedShifts,
            templateOpeningLine,
            voiceSettings?.phoebe_intro,
        ],
    );

    return (
        <div className="space-y-4">
            {onStepsChange ? (
                <OutreachSampleDetails
                    sampleData={sampleData}
                    onChange={handleSampleDataChange}
                />
            ) : null}
            {visibleSteps.length === 0 ? <EmptyStepsPreview /> : null}
            {visibleSteps.map((step, index) => {
                const effectiveStep = getEffectiveStep(step);
                const usesSingleClientTemplateEditor =
                    step.step_type === 'sms' &&
                    shouldUseSingleClientTemplateEditor({
                        step: effectiveStep,
                        selectedShifts,
                        usesGroupedTemplates: usesGroupedMultiShiftSms,
                    });
                const voiceVariables = isMultiShift
                    ? variableFlows.voiceMulti
                    : variableFlows.voiceSingle;
                const renderedStepSmsPreview =
                    step.step_type !== 'sms'
                        ? ''
                        : (smsStepPreviews[index] ?? '');
                const usesConsolidatedShiftTemplate =
                    step.step_type === 'sms' &&
                    !usesSingleClientTemplateEditor &&
                    shouldUseConsolidatedShiftTemplate({
                        outreachType,
                        selectedShifts,
                        shiftGroups: structuredShiftGroups,
                        step: effectiveStep,
                        timezone: organization?.timezone || 'UTC',
                    });
                const usesCompleteAllShiftsTemplate =
                    step.step_type === 'sms' &&
                    !usesSingleClientTemplateEditor &&
                    !usesConsolidatedShiftTemplate &&
                    shouldUseCompleteAllShiftsTemplate({
                        outreachType,
                        selectedShiftIds,
                        shiftGroups: structuredShiftGroups,
                        step: effectiveStep,
                    });
                const multiShiftHeaderVariables = usesConsolidatedShiftTemplate
                    ? variableFlows.smsConsolidatedShift
                    : usesSingleClientTemplateEditor
                      ? variableFlows.smsSingleClientGrouped
                      : usesGroupedMultiShiftSms
                        ? variableFlows.smsGrouped
                        : variableFlows.smsMultiShiftHeader;
                const multiShiftHeaderEditorValue =
                    getMultiShiftHeaderEditorValue(
                        effectiveStep,
                        usesGroupedMultiShiftSms,
                        usesSingleClientTemplateEditor,
                        usesCompleteAllShiftsTemplate,
                        usesConsolidatedShiftTemplate,
                        outreachType,
                    );
                const renderedStepVoicePreview =
                    step.step_type === 'voice' ? voicePreview(step, index) : '';
                const sampledStepSmsPreview = applyOutreachSampleData(
                    renderedStepSmsPreview,
                    sampleData,
                );
                const sampledStepVoicePreview = applyOutreachSampleData(
                    renderedStepVoicePreview,
                    sampleData,
                );
                const voiceEditorValue =
                    step.step_type !== 'voice'
                        ? ''
                        : getVoiceStepEditorValue(step, {
                              outreachType,
                              selectedShifts,
                              templateOpeningLine,
                              isPrimaryVoiceStep: index === firstVoiceStepIndex,
                              organization,
                              selectedShift: firstSelectedShift,
                          });
                const stepContent =
                    step.step_type === 'sms' ? (
                        onStepsChange ? (
                            outreachType === 'recurring_shift' &&
                            isMultiShift ? (
                                <MultiShiftSmsStep
                                    headerVariables={multiShiftHeaderVariables}
                                    itemVariables={variableFlows.smsMultiItem}
                                    smsPreview={sampledStepSmsPreview}
                                    headerValue={multiShiftHeaderEditorValue}
                                    onHeaderChange={(value) =>
                                        handleMultiShiftHeaderChange(
                                            index,
                                            value,
                                            usesCompleteAllShiftsTemplate,
                                            usesConsolidatedShiftTemplate,
                                        )
                                    }
                                    itemValue={
                                        effectiveStep.multi_shift_item_template ??
                                        ''
                                    }
                                    onItemChange={(value) =>
                                        handleMultiShiftItemChange(index, value)
                                    }
                                    variablePreviewValues={sampleData}
                                    clientReferenceTerm={clientReferenceTerm}
                                />
                            ) : outreachType === 'recurring_shift' ? (
                                <BaseSmsStep
                                    variables={variableFlows.smsSingle}
                                    smsPreview={sampledStepSmsPreview}
                                    value={getSingleShiftSmsEditorValue(
                                        effectiveStep,
                                        outreachType,
                                        {
                                            selectedShift: firstSelectedShift,
                                            organization,
                                            carePlanSummary:
                                                getCarePlanSummaryForShift(
                                                    firstSelectedShift,
                                                    perShiftCarePlans,
                                                ),
                                        },
                                    )}
                                    onChange={(value) =>
                                        handleSingleShiftSmsChange(index, value)
                                    }
                                    label="SMS Message"
                                    required={true}
                                    placeholder="Tailor the message to the shift's recurrence schedule"
                                    variablePreviewValues={sampleData}
                                    clientReferenceTerm={clientReferenceTerm}
                                />
                            ) : isMultiShift ? (
                                <MultiShiftSmsStep
                                    headerVariables={multiShiftHeaderVariables}
                                    itemVariables={variableFlows.smsMultiItem}
                                    smsPreview={sampledStepSmsPreview}
                                    headerValue={multiShiftHeaderEditorValue}
                                    onHeaderChange={(value) =>
                                        handleMultiShiftHeaderChange(
                                            index,
                                            value,
                                            usesCompleteAllShiftsTemplate,
                                            usesConsolidatedShiftTemplate,
                                        )
                                    }
                                    itemValue={
                                        effectiveStep.multi_shift_item_template ??
                                        ''
                                    }
                                    onItemChange={(value) =>
                                        handleMultiShiftItemChange(index, value)
                                    }
                                    variablePreviewValues={sampleData}
                                    clientReferenceTerm={clientReferenceTerm}
                                />
                            ) : (
                                <BaseSmsStep
                                    variables={variableFlows.smsSingle}
                                    smsPreview={sampledStepSmsPreview}
                                    value={getSingleShiftSmsEditorValue(
                                        effectiveStep,
                                        outreachType,
                                        {
                                            selectedShift: firstSelectedShift,
                                            organization,
                                            carePlanSummary:
                                                getCarePlanSummaryForShift(
                                                    firstSelectedShift,
                                                    perShiftCarePlans,
                                                ),
                                        },
                                    )}
                                    onChange={(value) =>
                                        handleSingleShiftSmsChange(index, value)
                                    }
                                    placeholder="Type here to change what Phoebe sends"
                                    variablePreviewValues={sampleData}
                                    clientReferenceTerm={clientReferenceTerm}
                                />
                            )
                        ) : (
                            <div className="space-y-2">
                                <div className="bg-muted/30 text-muted-foreground rounded p-1 text-sm whitespace-pre-wrap">
                                    <VariableTextDisplay
                                        value={sampledStepSmsPreview}
                                        variables={
                                            isMultiShift
                                                ? [
                                                      ...multiShiftHeaderVariables,
                                                      ...variableFlows.smsMultiItem,
                                                  ]
                                                : variableFlows.smsSingle
                                        }
                                    />
                                </div>
                            </div>
                        )
                    ) : onStepsChange ? (
                        <BaseVoiceStep
                            variables={voiceVariables}
                            voicePreview={sampledStepVoicePreview}
                            openingLineValue={voiceEditorValue}
                            onOpeningLineChange={(value) =>
                                handleVoiceOpeningLineChange(index, value)
                            }
                            control={control}
                            watch={watch}
                            selectedShiftIds={selectedShiftIds}
                            isLoadingInstructions={isLoadingInstructions}
                            outreachType={outreachType}
                            phoebeIntro={voiceSettings?.phoebe_intro}
                            showRecurrenceScheduleInput={
                                outreachType === 'recurring_shift' &&
                                index === firstVoiceStepIndex
                            }
                            structuredShiftGroups={structuredShiftGroups}
                            variablePreviewValues={sampleData}
                            clientReferenceTerm={clientReferenceTerm}
                        />
                    ) : (
                        <div className="bg-muted/30 text-muted-foreground rounded p-1 text-sm">
                            <VariableTextDisplay
                                value={sampledStepVoicePreview}
                                variables={voiceVariables}
                            />
                        </div>
                    );

                return (
                    <div
                        key={`${step.step_type}-${index}`}
                        className="bg-background rounded-lg border p-4 shadow-sm"
                    >
                        <div className="mb-2 flex items-start justify-between gap-4">
                            <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                                {getStepPreviewTitle(step)} Preview
                            </span>
                            {canEditStepStructure && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveStep(index)}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        {index > 0 && onStepsChange && (
                            <div className="mb-3 flex items-center gap-2">
                                <span className="text-muted-foreground text-sm">
                                    Wait
                                </span>
                                <OutreachStepDelayInput
                                    index={index}
                                    outreachType={outreachType}
                                    delayInMinutes={step.delay_in_minutes}
                                    inputLabel={`Step ${index + 1} wait`}
                                    onDelayChange={(delayInMinutes) =>
                                        handleDelayChange(index, delayInMinutes)
                                    }
                                />
                                <span className="text-muted-foreground text-sm">
                                    after the previous step
                                </span>
                            </div>
                        )}
                        {stepContent}
                    </div>
                );
            })}
            {canEditStepStructure && (
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleAddStep('sms')}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add SMS Step
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleAddStep('voice')}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Voice Step
                    </Button>
                </div>
            )}
            {spammyFollowUpConfirmationDialog}
        </div>
    );
}
